import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import * as Styles from './Chatbot.styles';

import withErrorBoundary from '@/utils/errorBoundary';
import {
  Button, IconChat, IconCheck, IconChevron, IconClose, IconDanger, IconReset, IconStars,
  Tooltip,
} from 'gatorcomponents';
import useLocale from '@/hooks/useLocale';
import { IconButton } from '@/pages/common/v1/IconButton';
import sendIcon from './media/send.svg';
import stopIcon from './media/stop.svg';
import { preChatActions } from '@/redux/modules';
import { useDispatch, useSelector } from 'react-redux';
import { IconThumbsUp } from './IconThumbsUp';
import { IconThumbsDown } from './IconThumbsDown';
import { useResize } from '@/hooks/useResize';

const elementsEnum = {
  intro: 'introduction',
  chatbot: 'chatbot',
};

const evaluationEnum = {
  positive: 'good',
  negative: 'bad',
};

const Chatbot = ({ handleStartZenDeskChat, dataToStartChat }) => {
  const { preChat: locale } = useLocale();

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [elementToShow, setElementToShow] = useState(undefined);
  const [renderIntro, setRenderIntro] = useState(true);
  const [openingChat, setOpeningChat] = useState(false);
  const [transitionToWidget, setTransitionToWidget] = useState(false);
  const [showTyping, setShowTyping] = useState(false);
  const [canStopMessage, setCanStopMessage] = useState(false);
  const [chatbotMinimized, setChatbotMinimized] = useState(false);
  const { lastChatbotMessage } = useSelector(state => state.preChat);
  const inputRef = useRef(null);
  const messagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isAddingMessageRef = useRef(canStopMessage);
  const timeoutsRef = useRef([]);
  const dispatch = useDispatch();

  const isOnboarding = window.location.pathname.includes('onboarding');
  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width <= 600;

  useEffect(() => {
    isAddingMessageRef.current = canStopMessage;
  }, [canStopMessage]);

  const hideScrollbar = messagesRef.current && messagesRef.current.scrollHeight <= messagesRef.current.clientHeight;

  const scrollToBottom = () => {
    messagesEndRef.current && messagesEndRef.current.scrollIntoView && messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const goToChat = () => {
    setOpeningChat(true);
    handleStartZenDeskChat && handleStartZenDeskChat(dataToStartChat);
    setTimeout(() => {
      setMessages([]);
      dispatch(preChatActions.chatbot.setChatbotMessage(null));
      setElementToShow(undefined);
      setOpeningChat(false);
      dispatch(preChatActions.preChat.setScreen('diagnostic'));
    }, 1200);

    setTimeout(() => {
      setTransitionToWidget(true);
    }, 700);

    setTimeout(() => {
      setTransitionToWidget(false);
    }, 2000);
  };

  const minimizeChatBot = () => {
    setChatbotMinimized(!chatbotMinimized);
  };

  const clearTimeouts = () => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  };

  const addMessageCharByChar = (message, delay = 16) => {
    clearTimeouts();

    const chars = message.split('');
    chars.forEach((char, index) => {
      const timeoutId = setTimeout(() => {
        if (!isAddingMessageRef.current) return;

        setMessages((prevMessages) => {
          const lastMessage = prevMessages[prevMessages.length - 1];
          const newMessage = {
            ...lastMessage,
            text: lastMessage.text + char,
          };

          return [...prevMessages.slice(0, -1), newMessage];
        });

        if (index === chars.length - 1) {
          setCanStopMessage(false);
          clearTimeouts();
        }
      }, index * delay);

      timeoutsRef.current.push(timeoutId);
    });
  };

  const markdownToHtml = (markdown) => {
    let html = markdown;

    // Convert headers
    html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

    // Convert bold text
    html = html.replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>');

    // Convert italic text
    html = html.replace(/\*(.*?)\*/gim, '<em>$1</em>');
    html = html.replace(/_(.*?)_/gim, '<em>$1</em>');

    // Convert blockquotes
    html = html.replace(/^\s*>\s+(.*)$/gim, '<blockquote>$1</blockquote>');

    // Convert inline code
    html = html.replace(/`([^`]+)`/gim, '<code>$1</code>');

    // Convert code blocks
    html = html.replace(/```([^`]+)```/gim, '<pre><code>$1</code></pre>');

    // Convert links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2">$1</a>');

    // Convert images
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/gim, '<img alt="$1" src="$2" />');

    // Convert unordered lists
    html = html.replace(/^\s*[-+*]\s+(.*)$/gim, '<ul><li>$1</li></ul>');
    html = html.replace(/<\/ul>\s*<ul>/gim, ''); // Remove consecutive <ul> tags

    // Convert ordered lists
    html = html.replace(/^\s*\d+\.\s+(.*)$/gim, '<ol><li>$1</li></ol>');
    html = html.replace(/<\/ol>\s*<ol>/gim, ''); // Remove consecutive <ol> tags

    // Convert line breaks
    html = html.replace(/\n/g, '');

    return html.trim();
  };

  const renderMessage = (message) => {
    const formattedMessage = markdownToHtml(message);
    return <Styles.BotMessage dangerouslySetInnerHTML={{ __html: formattedMessage }} />;
  };


  const receiveNewMessage = () => {
    if (lastChatbotMessage && showTyping) {
      if (lastChatbotMessage.transferToHuman) {
        setMessages([...messages, {
          text: '', origin: 'bot', id: Date.now(),
        }]);
        addMessageCharByChar(locale.chatbot.transferToHuman);
        setTimeout(goToChat, 4000);
      } else if (lastChatbotMessage.error === 'retry') {
        setMessages([...messages, {
          text: locale.chatbot.errorMessageAgain, origin: 'bot', id: Date.now(), error: lastChatbotMessage.error,
        }]);
        setCanStopMessage(false);
      } else if (lastChatbotMessage.error === 'first') {
        setMessages([...messages, {
          text: locale.chatbot.errorMessage, origin: 'bot', id: Date.now(), error: lastChatbotMessage.error,
        }]);
        setCanStopMessage(false);
      } else {
        setMessages([...messages, { ...lastChatbotMessage, text: '' }]);
        addMessageCharByChar(lastChatbotMessage.text || '');
      }
    }
    setShowTyping(false);
  };

  useEffect(() => {
    receiveNewMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastChatbotMessage]);

  const startMessage = useCallback(() => {
    setTimeout(() => {
      setMessages([
        {
          text: locale.chatbot.welcomeMessage,
          origin: 'bot',
          id: Date.now(),
          firstMessage: true,
        },
      ]);
    }, 1000);
  }, [locale.chatbot.welcomeMessage]);

  useEffect(() => {
    startMessage();
  }, [startMessage]);

  const shouldShowIntroduction = () => {
    const skipIntroduction = localStorage.getItem('skipCortexIntroduction');
    if (skipIntroduction) {
      setTimeout(() => setElementToShow(elementsEnum.chatbot), 50);
      setRenderIntro(false);
    } else {
      setTimeout(() => setElementToShow(elementsEnum.intro), 50);
    }
  };

  useEffect(() => {
    shouldShowIntroduction();
    dispatch(preChatActions.chatbot.startRequest());
  }, [dispatch]);

  const markChatAsRead = () => {
    localStorage.setItem('skipCortexIntroduction', 'true');
    setElementToShow(undefined);
    setTimeout(() => {
      setRenderIntro(false);
      setElementToShow(elementsEnum.chatbot);
    }, 200);
  };

  const sendMessage = () => {
    if (canStopMessage) {
      setCanStopMessage(false);
      clearTimeouts();
      setShowTyping(false);
      return;
    }

    if (!inputValue) return;
    setMessages([...messages, { text: inputValue, origin: 'user', id: Date.now() }]);
    setInputValue('');
    inputRef.current.focus();
    dispatch(preChatActions.chatbot.sendMessage({ message: inputValue }));

    setShowTyping(true);
    setCanStopMessage(true);
  };

  const chatReset = () => {
    setMessages([]);
    setInputValue('');
    inputRef.current.focus();
    startMessage();
    dispatch(preChatActions.chatbot.startRequest());
  };

  const closeIconClick = () => {
    setShowCloseModal(true);
  };

  const finishChat = () => {
    setShowCloseModal(false);
    chatReset();
    dispatch(preChatActions.preChat.setScreen('diagnostic'));
  };

  const tryMessageAgain = () => {
    dispatch(preChatActions.chatbot.sendMessage({ message: lastChatbotMessage.message, retry: true }));
    setShowTyping(true);
    setCanStopMessage(true);
  };

  const answerEvaluation = (index, evaluation, messageId) => {
    setMessages((prevMessages) => {
      const newMessages = [...prevMessages];
      newMessages[index] = { ...newMessages[index], evaluation };
      return newMessages;
    });

    dispatch(preChatActions.chatbot.evaluateAnswerRequest({ evaluation, messageId }));
  };

  return (
    <>
      {renderIntro && (
        <Styles.ChatBotIntroWrapper showIntro={elementToShow === elementsEnum.intro}>
          <Styles.ChatBotIntroduction>
            <Styles.IconCloseWrapper>
              <IconButton
                icon={<IconClose color="highPure" size="md" />}
                onClick={markChatAsRead}
              />
            </Styles.IconCloseWrapper>
            <Styles.IconWrapper>
              <IconStars color="highPure" size="lg" />
              <Styles.BetaLabel>
                Beta
              </Styles.BetaLabel>
            </Styles.IconWrapper>
            <Styles.Title>
              {locale.chatbot.title}
            </Styles.Title>
            <Styles.Description>
              {locale.chatbot.description}
            </Styles.Description>
            {locale.chatbot.cards.map(({ cardText, id }) => (
              <Styles.Card key={id}>
                <Styles.IconCheckWrapper>
                  <IconCheck color="highPure" size="sm" />
                </Styles.IconCheckWrapper>
                <Styles.CardText>
                  {cardText}
                </Styles.CardText>
              </Styles.Card>
            ))}
            <Styles.ButtonWrapper>
              <Button
                label={locale.chatbot.startChat}
                size="large"
                variant="secondary"
                onClick={markChatAsRead}
              />
            </Styles.ButtonWrapper>
          </Styles.ChatBotIntroduction>
        </Styles.ChatBotIntroWrapper>
      )}

      {!renderIntro && (
        <>
          <Styles.ChatWindow
            id="chatbot-window"
            showCloseModal={showCloseModal}
            showChat={elementToShow === elementsEnum.chatbot}
            chatbotMinimized={chatbotMinimized}
            openingChat={openingChat}
            transitionToWidget={transitionToWidget}
            isOnboarding={isOnboarding}
            isMobile={isMobile}
          >
            <Styles.ChatHeader>
              <Styles.ChatHeaderTitle>
                <IconStars color="highPure" size="md" />
                Cortex
                <Styles.BetaLabel small>
                  Beta
                </Styles.BetaLabel>
              </Styles.ChatHeaderTitle>
              <Styles.ChatHeaderActions>
                <Tooltip content={locale.chatbot.resetChat} preferredPosition="bottomLeft">
                  <IconButton
                    icon={<IconReset color="highPure" />}
                    onClick={chatReset}
                  />
                </Tooltip>
                <Tooltip content={locale.chatbot.finishChatTooltip} preferredPosition="bottomLeft">
                  <IconButton
                    icon={<IconClose color="highPure" />}
                    onClick={closeIconClick}
                  />
                </Tooltip>
              </Styles.ChatHeaderActions>
            </Styles.ChatHeader>

            <Styles.ChatBody>
              <Styles.ChatMessages hideScrollbar={hideScrollbar} ref={messagesRef}>
                {messages.map((message, index) => (
                  <Styles.ChatMessageWrapper fromUser={message.origin === 'user'} key={message.id}>
                    {message.origin === 'bot' && (
                      <Styles.IconStarsMessage>
                        <IconStars color="highPure" size="sm" />
                      </Styles.IconStarsMessage>
                    )}
                    <Styles.ChatMessage fromUser={message.origin === 'user'}>
                      {renderMessage(message.text)}

                      {message.error && (
                        <Styles.ErrorButtonWrapper>
                          <Button label={locale.chatbot.tryAgain} onClick={tryMessageAgain} />
                        </Styles.ErrorButtonWrapper>
                      )}

                      {message.error === 'retry' && (
                        <Styles.ErrorButtonWrapper isErrorRetry>
                          <Button label={locale.chatbot.talkWithHuman} onClick={goToChat} variant="secondary" loading={openingChat} />
                        </Styles.ErrorButtonWrapper>
                      )}

                      {!message.firstMessage && message.origin === 'bot' && (
                        <Styles.FeedbackWrapper>
                          <Tooltip content={locale.chatbot.notHelpedMe} preferredPosition="topLeft">
                            <Styles.FeedbackButton
                              onClick={() => answerEvaluation(index, evaluationEnum.negative, message.id)}
                            >
                              <IconThumbsDown selected={message.evaluation === evaluationEnum.negative} />
                            </Styles.FeedbackButton>
                          </Tooltip>

                          <Tooltip content={locale.chatbot.helpedMe} preferredPosition="topLeft">
                            <Styles.FeedbackButton
                              onClick={() => answerEvaluation(index, evaluationEnum.positive, message.id)}
                            >
                              <IconThumbsUp selected={message.evaluation === evaluationEnum.positive} />
                            </Styles.FeedbackButton>
                          </Tooltip>
                        </Styles.FeedbackWrapper>
                      )}
                    </Styles.ChatMessage>
                  </Styles.ChatMessageWrapper>
                ))}

                {showTyping && (
                  <Styles.ChatMessageWrapper>
                    <Styles.ChatMessage fromUser={false}>
                      <Styles.TypingDots>
                        <Styles.Dot />
                        <Styles.Dot />
                        <Styles.Dot />
                      </Styles.TypingDots>
                    </Styles.ChatMessage>
                  </Styles.ChatMessageWrapper>
                )}
                <Styles.ChatEnd ref={messagesEndRef} />
              </Styles.ChatMessages>
            </Styles.ChatBody>

            <Styles.ChatFooter>
              <Styles.ChatInputWrapper>
                <Styles.ChatInput
                  placeholder={locale.chatbot.inputPlaceholder}
                  onChange={event => setInputValue(event.target.value)}
                  onKeyDown={event => event.key === 'Enter' && sendMessage()}
                  value={inputValue}
                  ref={inputRef}
                />

                {canStopMessage && (
                  <Tooltip content={locale.chatbot.stopMessage} preferredPosition="topLeft">
                    <IconButton
                      icon={(
                        <Styles.Icon
                          src={stopIcon}
                          alt="Stop"
                        />
                      )}
                      onClick={sendMessage}
                    />
                  </Tooltip>
                )}
                {!canStopMessage && (
                  <IconButton
                    icon={(
                      <Styles.Icon
                        src={sendIcon}
                        alt="Send"
                      />
                    )}
                    onClick={sendMessage}
                  />
                )}
              </Styles.ChatInputWrapper>


              <Styles.AlertMessage>
                {locale.chatbot.aiAlert}
              </Styles.AlertMessage>
            </Styles.ChatFooter>

            {showCloseModal && (
              <Styles.ModalWrapper>
                <Styles.Modal>
                  <Styles.ModalCloseWrapper>
                    <IconButton
                      icon={<IconClose color="lowLight" />}
                      onClick={() => setShowCloseModal(false)}
                    />
                  </Styles.ModalCloseWrapper>

                  <IconDanger color="primaryExtraDark" size="xl" />
                  <Styles.ModalTitle>
                    {locale.chatbot.closeChat}
                  </Styles.ModalTitle>
                  <Styles.ModalAlert>
                    {locale.chatbot.closeChatAlert}
                  </Styles.ModalAlert>

                  <Styles.ModalActions>
                    <Button
                      label={locale.chatbot.cancel}
                      variant="tertiary"
                      onClick={() => setShowCloseModal(false)}
                    />

                    <Button
                      label={locale.chatbot.finishChat}
                      onClick={finishChat}
                    />
                  </Styles.ModalActions>
                </Styles.Modal>
              </Styles.ModalWrapper>
            )}
          </Styles.ChatWindow>

          {!openingChat && (
            <Styles.ChatButtonContainer
              openingChat={openingChat}
              isOnboarding={isOnboarding}
              isMobile={isMobile}
            >
              <Button
                testId="chat-button"
                size="large"
                label={!chatbotMinimized ? '' : 'Chat'}
                iconLeft={!chatbotMinimized ? <IconChevron direction="bottom" /> : <IconChat color="highPure" />}
                onClick={minimizeChatBot}
                notFillSVG={chatbotMinimized}
              />
            </Styles.ChatButtonContainer>
          )}
        </>
      )}
    </>
  );
};

export default withErrorBoundary(Chatbot, 'Chatbot');
